import EditOutlined from '@ant-design/icons/EditOutlined'
import type { InputRef } from 'antd'
import { Button, Input } from 'antd'
import { convertKeyToLabel } from 'features/dynamicOntology'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

export interface EditableLabelProps {
  label?: string
  propertyKey?: string
  setLabel?: Dispatch<SetStateAction<string>>
  disableEdit?: boolean
  keyPrefix?: string
}

const EditableLabelContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

/**
 * Label used in the RecursivePropertyView, which renders a property label,
 * and if a 'setLabel' prop is provided, adds a button to edit the label.
 * When editing, the label is rendered as an input field, and when not editing,
 * the label is rendered as a span.
 */
export const EditableLabel: FC<EditableLabelProps> = ({
  keyPrefix = '',
  label,
  propertyKey,
  setLabel,
  disableEdit = false,
}) => {
  const [draft, setDraft] = useState<string | undefined>(undefined)
  const inputRef = useRef<InputRef>(null)
  const canEdit = Boolean(setLabel) && !disableEdit && propertyKey !== undefined
  const isEditing = draft !== undefined

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
    }
  }, [isEditing])

  const displayText = useMemo(() => {
    return label ?? (propertyKey ? convertKeyToLabel(propertyKey) : '')
  }, [label, propertyKey])

  const commitLabel = useCallback(() => {
    try {
      if (!setLabel || !canEdit || draft === undefined) {
        return
      }

      const trimmed = draft.trim()

      if (!trimmed) {
        return
      }

      setLabel(trimmed)
    } finally {
      setDraft(undefined)
    }
  }, [canEdit, draft, setLabel])

  const toggleEditing = useCallback(() => {
    if (!canEdit) {
      return
    }
    if (isEditing) {
      commitLabel()
    } else {
      setDraft(propertyKey ? propertyKey.substring(keyPrefix.length) : '')
    }
  }, [canEdit, commitLabel, isEditing, keyPrefix, propertyKey])

  return (
    <EditableLabelContainer>
      {canEdit && isEditing ? (
        <Input
          ref={inputRef}
          size='small'
          style={{ width: '20rem' }}
          value={draft ?? ''}
          onChange={(event) => setDraft(event.target.value)}
          onPressEnter={commitLabel}
          placeholder='property_key'
        />
      ) : (
        <span>{displayText}</span>
      )}
      {canEdit && (
        <Button
          type='text'
          size='small'
          icon={
            <EditOutlined
              style={{
                color: isEditing
                  ? CantileverTheme.colors.attention['100']
                  : undefined,
              }}
            />
          }
          onClick={toggleEditing}
          style={{ padding: 0 }}
          aria-label={isEditing ? 'Save label' : 'Edit label'}
        />
      )}
    </EditableLabelContainer>
  )
}
